// @flow
import { all, fork, put, takeEvery, call } from "redux-saga/effects";

import {
  login as loginApi,
  logout as logoutApi,
  confirmMfa as confirmMfaApi,
  forgotPassword as forgotPasswordApi,
  newPassword as newPasswordApi,
  verifyEmail as verifyEmailApi,
  invitationSignup as invitationSignupApi,
  signup as signupApi,
} from "../../helpers/";

import { APICore, setAuthorization } from "../../helpers/api/apiCore";
import { authApiResponseSuccess, authApiResponseError } from "./actions";
import { AuthActionTypes } from "./constants";

const api = new APICore();

/**
 * Login the user
 * @param {*} payload - username and password
 */
function* login({ payload: { username, password } }) {
  try {
    const response = yield call(loginApi, { username, password });
    const data = response[1];

    // TOTP-enrolled user: signIn returned a challenge, not a session. Signal the
    // challenge step and stop — do NOT persist a session yet (confirmMfa completes it).
    if (data && data.mfaRequired) {
      yield put(authApiResponseSuccess(AuthActionTypes.LOGIN_USER, data));
      return;
    }

    api.setLoggedInUserInLocalStorage(data);
    setAuthorization(data["token"]);
    yield put(authApiResponseSuccess(AuthActionTypes.LOGIN_USER, data));
  } catch (error) {
    yield put(authApiResponseError(AuthActionTypes.LOGIN_USER, error[1]));
    //api.setLoggedInUser(null);
    api.setLoggedInUserInLocalStorage(null);
    setAuthorization(null);
  }
}

/**
 * Completes a TOTP MFA challenge started by login().
 * @param {*} payload - the 6-digit authenticator code
 */
function* confirmMfa({ payload: { code } }) {
  try {
    const response = yield call(confirmMfaApi, { code });
    const user = response[1];
    api.setLoggedInUserInLocalStorage(user);
    setAuthorization(user["token"]);
    yield put(authApiResponseSuccess(AuthActionTypes.CONFIRM_MFA, user));
  } catch (error) {
    yield put(authApiResponseError(AuthActionTypes.CONFIRM_MFA, error[1]));
    api.setLoggedInUserInLocalStorage(null);
    setAuthorization(null);
  }
}

/**
 * Logout the user
 */
function* logout() {
  try {
    const response = yield call(logoutApi);
    console.log("logout response", response);
    api.setLoggedInUser(null);
    api.setLoggedInUserInLocalStorage(null);
    setAuthorization(null);
    yield put(authApiResponseSuccess(AuthActionTypes.LOGOUT_USER, {}));
  } catch (error) {
    yield put(authApiResponseError(AuthActionTypes.LOGOUT_USER, error));
  }
}

function* signup({
  payload: { firstName, lastName, companyName, email, password, country, type },
}) {
  try {
    yield call(signupApi, {
      firstName,
      lastName,
      companyName,
      email,
      password,
      country,
      type,
    });
    api.setLoggedInUserInLocalStorage(null);
    setAuthorization(null);
    yield put(authApiResponseSuccess(AuthActionTypes.SIGNUP_USER, null));
  } catch (error) {
    yield put(
      authApiResponseError(AuthActionTypes.SIGNUP_USER, error[1].message),
    );
    //api.setLoggedInUser(null);
    api.setLoggedInUserInLocalStorage(null);
    setAuthorization(null);
  }
}

function* invitationSignup({
  payload: { firstName, lastName, email, password, invitationId, language },
}) {
  try {
    yield call(invitationSignupApi, {
      firstName,
      lastName,
      email,
      password,
      invitationId,
      language,
    });
    api.setLoggedInUserInLocalStorage(null);
    setAuthorization(null);
    yield put(
      authApiResponseSuccess(AuthActionTypes.INVITATION_SIGNUP_USER, null),
    );
  } catch (error) {
    yield put(
      authApiResponseError(
        AuthActionTypes.INVITATION_SIGNUP_USER,
        error[1].message,
      ),
    );
    api.setLoggedInUserInLocalStorage(null);
    setAuthorization(null);
  }
}

function* forgotPassword({ payload: { username } }) {
  try {
    const response = yield call(forgotPasswordApi, { username });
    yield put(
      authApiResponseSuccess(AuthActionTypes.FORGOT_PASSWORD, response[1]),
    );
  } catch (error) {
    yield put(
      authApiResponseError(AuthActionTypes.FORGOT_PASSWORD, error[1].message),
    );
  }
}

function* newPassword({ payload: { email, code, newPassword } }) {
  try {
    const response = yield call(newPasswordApi, {
      email,
      code,
      newPassword,
    });
    yield put(
      authApiResponseSuccess(AuthActionTypes.SET_NEW_PASSWORD, response[1]),
    );
  } catch (error) {
    yield put(
      authApiResponseError(AuthActionTypes.SET_NEW_PASSWORD, error[1].message),
    );
  }
}

function* verifyEmail({ payload: { email, code } }) {
  try {
    const response = yield call(verifyEmailApi, {
      email,
      code,
    });
    yield put(
      authApiResponseSuccess(AuthActionTypes.VERIFY_EMAIL, response[1]),
    );
  } catch (error) {
    yield put(
      authApiResponseError(AuthActionTypes.VERIFY_EMAIL, error[1].message),
    );
  }
}


function* watchLoginUser() {
  yield takeEvery(AuthActionTypes.LOGIN_USER, login);
}

function* watchConfirmMfa() {
  yield takeEvery(AuthActionTypes.CONFIRM_MFA, confirmMfa);
}

function* watchLogout() {
  yield takeEvery(AuthActionTypes.LOGOUT_USER, logout);
}

function* watchSignup() {
  yield takeEvery(AuthActionTypes.SIGNUP_USER, signup);
}

function* watchInvitationSignup() {
  yield takeEvery(AuthActionTypes.INVITATION_SIGNUP_USER, invitationSignup);
}

function* watchForgotPassword() {
  yield takeEvery(AuthActionTypes.FORGOT_PASSWORD, forgotPassword);
}

function* watchNewPassword() {
  yield takeEvery(AuthActionTypes.SET_NEW_PASSWORD, newPassword);
}


function* watchVerifyEmail() {
  yield takeEvery(AuthActionTypes.VERIFY_EMAIL, verifyEmail);
}

function* authSaga() {
  yield all([
    fork(watchLoginUser),
    fork(watchConfirmMfa),
    fork(watchLogout),
    fork(watchSignup),
    fork(watchInvitationSignup),
    fork(watchNewPassword),
    fork(watchVerifyEmail),
    fork(watchForgotPassword),
  ]);
}

export default authSaga;
